import React, { useEffect, useState, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import CartItems from './CartItems';
import Voucher from './Voucher';
import Totals from './Totals';
import KlarnaCheckout from './KlarnaCheckout';
import checkoutAnalytics from './checkoutAnalytics';
import Ingrid from './Ingrid';
import Input from '../Elements/Input/Input';
import Button from '../Elements/Button/Button';
import Select from '../Elements/Select/Select';
import Radio from '../Elements/Radio/Radio';
import Checkbox from '../Elements/Checkbox/Checkbox';
import TemplateParser from '../Templates/TemplateParser';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Overlay from '../../hocs/Overlay/Overlay';
import {
	addVoucher,
	removeVoucher,
	initiatedScript,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	ingridLoaded,
	ingridUnloaded,
	updateKlarna,
	updatePaymentMethod,
	purchase,
} from '../../actions/checkout';
import { goToPage, preloadPage } from '../../actions/environment';
import { purifier, addEvent, removeEvent, hasClass, fixBackgroundScroll, height } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { camelCase, upperCaseFirst, fieldValidation, formatPrice } from '../../utils/string';
import {
	paymentMethodsMapping,
	paymentMethodsOrder,
	addressFieldOrder,
} from '../../config';
import s from './Selection.scss';

const Selection = ({ checkout, setup, pageData, ...props }) => {
	const { selection = {}, shippingAvailable, paymentUpdating = false, ingridExists = false } = checkout;
	const { paymentFields, paymentMethod, shippingMethods = [] } = selection;
	let { paymentMethods = [] } = selection;
	const states = setup.countries[setup.country].states ? Object.keys(setup.countries[setup.country].states).map((state) => ({ key: state, value: setup.countries[setup.country].states[state] })) : null;

	const [errors, updateErrors] = useState({
		address: {},
		shippingAddress: {},
	});
	// const initialState = {
	// 	interest: ['women', 'men'],
	// };
	const [analytics, analyticsShown] = useState(false);
	// const [fields, updateFields] = useState(initialState);
	const [counter, updateCounter] = useState(5);
	const [overlayShowing, toggleOverlay] = useState('');
	const policyLinks = useRef(null);
	const [summarySideTall, updateSummaryFit] = useState(false);
	const summarySide = useRef(null);

	useEffect(() => {
		if (summarySide.current) {
			const h = height(summarySide.current);
			updateSummaryFit(h > window.innerHeight - 10);

			addEvent(window, 'resize.summary', () => {
				window.requestAnimationFrame(() => {
					updateSummaryFit(h > window.innerHeight - 10);
				});
			});
		}
	});

	useEffect(() => {
		let counterTimer = null;
		if (!selection?.items || (selection.items && selection.items.length === 0)) {
			counterTimer = window.setInterval(() => {
				updateCounter((prevState) => {
					if (prevState === 0) {
						props.goToPage('/');
						return 0;
					}
					return prevState - 1;
				});
			}, 1000);
		}

		if (policyLinks.current) {
			addEvent(policyLinks.current, 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					e.stopPropagation();

					const loadPage = $link.getAttribute('href');
					props.preloadPage(loadPage);
					toggleOverlay(loadPage);
					fixBackgroundScroll(true);
				}
			});
		}

		return () => {
			if (policyLinks.current) removeEvent(policyLinks.current, 'click.linkCapture');
			window.clearInterval(counterTimer);
		};
	}, [selection]);

	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (!analytics && checkout.selection.items && checkout.selection.items.length > 0) {
			checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals);
			analyticsShown(true);
			const hasStripePaymentIntentsLoaded = checkout.selection.paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
			if (Object.keys(hasStripePaymentIntentsLoaded).length > 0) props.purchase('selection', true, paymentMethodsMapping.stripePaymentIntents);
		}
	}, [checkout.selection]);

	let hasShipping = false;
	if (paymentFields) {
		Object.keys(paymentFields.shippingAddress).forEach((field) => {
			if (paymentFields.shippingAddress[field].visible) hasShipping = true;
		});
	}

	const closeOverlayHandler = () => {
		toggleOverlay('');
		fixBackgroundScroll(false);
	};

	const countries = Object.keys(setup.countries).filter((country) => setup.countries[country].shipTo).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: setup.countries[country].name }] : [...acc]), []);
	const onCountryChange = (id, value) => {
		props.changeAddressInput('address', 'country', value);
		props.changeAddressInput('shippingAddress', 'country', value);
		props.changeCountry(value);
		props.updatePaymentMethod();
		props.updateKlarna();
	};
	const onShippingCountryChange = (id, value) => {
		props.changeAddressInput('shippingAddress', 'country', value);
		props.changeCountry(value);
		props.updatePaymentMethod();
		props.updateKlarna();
	};

	paymentMethods = paymentMethods.sort((a, b) => {
		const posA = paymentMethodsOrder.indexOf(a.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(a.paymentMethod) : 1000;
		const posB = paymentMethodsOrder.indexOf(b.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(b.paymentMethod) : 1000;
		return posA - posB;
	});

	// const availablePaymentMethods = paymentMethods.reduce((acc, method) => [...acc, { key: method.paymentMethod, value: (setup.statics[camelCase(method.name)] ? translate(setup, camelCase(method.name)) : method.name) }], []) || [];
	const hasKlarna = paymentMethods.filter((method) => method.paymentMethod === paymentMethodsMapping.klarnaCheckout) || false;
	const hasAdyenDropIn = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.adyenDropIn ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasAdyenCheckout = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.adyenCheckout ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripeCheckout = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripeCheckout ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripePaymentIntents = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;

	// const onPaymentMethodChange = (value) => {
	// 	props.changePaymentMethod(value);
	// };

	const availableShippingMethods = shippingMethods.reduce((acc, method) => [...acc, { key: method.shippingMethod, value: (setup.statics[camelCase(method.name)] ? translate(setup, camelCase(method.name)) : method.name.replace(/^\d+/, '')), disabled: shippingAvailable[method.shippingMethod] === false }], []) || [];

	const onShippingMethodChange = (value) => {
		props.changeShippingMethod(value);
	};

	const getIdAndGroup = (id) => {
		if (id.indexOf('shippingAddress') !== -1) return ['shippingAddress', String(id).replace('shippingAddress-', '')];
		if (id.indexOf('address') !== -1) return ['address', String(id).replace('address-', '')];
		return [false, String(id)];
	};

	const onAddressInputChange = (id, value) => {
		const [group, field] = getIdAndGroup(id);
		props.changeAddressInput(group, field, value);
		updateErrors((prevState) => {
			if (group) {
				const { [field]: removed, ...nextState } = prevState[group];
				return { ...prevState, [group]: { ...nextState } };
			}
			const { [field]: removed, ...nextState } = prevState;
			return { ...nextState };
		});
	};

	const onAddressInputBlur = (id, value, all = false) => {
		const [group, field] = getIdAndGroup(id);

		onAddressInputChange(id, value);

		if (
			fieldValidation(value, field) // Is field invalid?
			&& ( // And is required
				!selection?.paymentFields?.[group]?.[field] || selection?.paymentFields?.[group]?.[field]?.required // Is in a group.
				|| !selection?.paymentFields?.[field] || selection?.paymentFields?.[field]?.required // Is not in a group.
				|| (field === 'state' && states) || field === 'country'
			)
			&& (group !== 'shippingAddress' || (group === 'shippingAddress' && selection.useShippingAddress)) // And is address or is using shipping address.
		) {
			updateErrors((prevState) => {
				if (group) {
					return {
						...prevState,
						[group]: {
							...prevState[group],
							[field]: translate(setup, `error${fieldValidation(value, field)}`),
						},
					};
				}
				return {
					...prevState,
					[field]: translate(setup, `error${fieldValidation(value, field)}`),
				};
			});
			return true;
		}

		if (!fieldValidation(value, field) && !all) {
			// Is valid update methods.
			props.updatePaymentMethod();
			props.updateKlarna();
		}
		return false;
	};

	const onBooleanChange = (id, value) => {
		props.booleanChange(id);
		onAddressInputBlur(id, value);
		if (paymentMethod === paymentMethodsMapping.klarnaCheckout && id === 'subscribeToNewsletter') props.updateKlarna();
		if (id === 'useShipping' && value === false) {
			props.changeAddressInput('address', 'country', checkout.payment.shippingAddress.country);
		}
		props.updatePaymentMethod();
	};

	const onPurchase = (e) => {
		e.preventDefault();

		// Ensure that we are using data from the fields as it may be autofilled.
		const $inputs = e.currentTarget.querySelectorAll('input,select');
		let hasErrors = false;

		[...$inputs].forEach(($input) => {
			const id = $input.getAttribute('id');
			let value = $input.value;
			if ($input.type === 'checkbox' && !$input.checked) value = 0;
			const returnErrors = onAddressInputBlur(id, value, true);
			if (returnErrors) hasErrors = true;
		});

		if (hasErrors || Object.keys(errors.address).length > 0 || Object.keys(errors.shippingAddress).length > 0 || Object.keys(errors).length > 2) return;

		props.updatePaymentMethod();
		props.updateKlarna();
		props.purchase();
	};

	const vouchersSet = (checkout?.selection?.discounts?.vouchers || []).length > 0 ? checkout.selection.discounts.vouchers.map((v) => v.voucher) : [];
	const donationHandler = (e) => {
		const value = e.currentTarget.value;
		if (vouchersSet.indexOf(value) === -1) {
			props.addVoucher(value);
		} else {
			props.removeVoucher(value);
		}
	};
	const voucherAmount = (match) => {
		const [nokSek, other] = match.slice(1, -1).split('|');
		let returnAmount = other;
		if (parseInt(setup.pricelist, 10) === 19 || parseInt(setup.pricelist, 10) === 25) returnAmount = nokSek;
		console.log('DEBUG: setup.currency', setup.currency);
		return formatPrice(setup, returnAmount, false, true);
	};

	/*
	const newsletterInterestChangeHandler = (id) => {
		const selectedInterest = id.slice(0, id.indexOf('Newsletter'));
		updateFields((prevState) => {
			let interest = prevState.interest.slice(0);
			if (interest.indexOf(selectedInterest) === -1) interest = [...interest, selectedInterest];
			else if (interest.length > 1) {
				interest = [...interest.slice(0, interest.indexOf(selectedInterest)), ...interest.slice(interest.indexOf(selectedInterest) + 1)];
			} else {
				interest = [...initialState.interest.slice(0, initialState.interest.indexOf(selectedInterest)), ...initialState.interest.slice(initialState.interest.indexOf(selectedInterest) + 1)];
			}

			return {
				...prevState,
				interest,
			};
		});
	};
	*/

	return (
		<main role="main">
			{
				!selection.items || selection.items.length === 0 ? (
					<div className={s.selection}>
						<h1 className={[s.selection_title, s.selection_title__empty].join(' ')}>
							{`${translate(setup, 'checkoutNoItems')}
							${translate(setup, 'checkoutEmptyReturningHome')} ${counter}`}
						</h1>
					</div>
				) : (
					<div className={s.selection}>
						<div className={[s.selection_summary, (summarySideTall ? s.selection_summary__tall : null)].join(' ')} ref={summarySide}>
							<h1 className={s.selection_title}><span>{ translate(setup, 'checkoutYourItemsTitle') }</span></h1>
							<div className={s.selection_part}>
								<ErrorBoundary message="Product data missing" debugData={selection}>
									<div className={s.selection_part_cartOverflow}>
										<CartItems currentPage="selection" />
									</div>
									<div className={s.selection_part_voucher}>
										<Voucher setup={setup} checkout={checkout} />
									</div>
									<div className={s.selection_part_totals}>
										<Totals setup={setup} selection={selection} />
									</div>
									{ setup.statics.checkoutCustomerService ? <div className={s.selection_part_customerService} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutCustomerService')) }} /> : null }
								</ErrorBoundary>
							</div>
							<h1 className={[s.selection_title, s.selection_title__donations].join(' ')}><span>{ translate(setup, 'checkoutYourDonations') }</span></h1>
							<div className={s.selection_part}>
								<div className={s.selection_part_donations} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutDonationPreface')) }} />
								<ErrorBoundary message="Product data missing" debugData={selection}>
									<Button variantClasses={[(vouchersSet.indexOf('donationdoctorswithoutborders') !== -1 ? 'button__inverse' : null)]} type="button" clickEventHandler={donationHandler} value="donationdoctorswithoutborders">{ translate(setup, 'checkoutDonation1').replace(/\(.*\)/, voucherAmount) }</Button>
									<Button variantClasses={[(vouchersSet.indexOf('donationdoctorswithoutborders2') !== -1 ? 'button__inverse' : null)]} type="button" clickEventHandler={donationHandler} value="donationdoctorswithoutborders2">{ translate(setup, 'checkoutDonation2').replace(/\(.*\)/, voucherAmount) }</Button>
									<Button variantClasses={[(vouchersSet.indexOf('donationdoctorswithoutborders3') !== -1 ? 'button__inverse' : null)]} type="button" clickEventHandler={donationHandler} value="donationdoctorswithoutborders3">{ translate(setup, 'checkoutDonation3').replace(/\(.*\)/, voucherAmount) }</Button>
								</ErrorBoundary>
							</div>
						</div>
						{
							setup.unableToSendToCountry ? (
								<div className={s.selection_info}>
									<h2 className={s.selection_title}><span>{ translate(setup, 'checkoutDeliveryTitleChangeCountry') }</span></h2>
									<div className={s.selection_part}>
										<div className={s.selection_part_inner}>
											<div className={s.selection_part_address}>
												<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} label={translate(setup, 'labelCountryShipping')} />
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className={s.selection_info}>
									{
										Object.keys(hasStripePaymentIntents).length > 0 && (
											<div className={s.selection_stripe}>
												<div id="stripePaymentIntentsContainer" />
												<div className={s.selection_stripe_paymentButton} id="stripeCheckoutButton" />
												<h2 className={s.selection_title}><span>{ translate(setup, 'checkoutExpressTitle') }</span></h2>
											</div>
										)
									}
									<h2 className={s.selection_title}><span>{ translate(setup, 'checkoutInformationTitle') }</span></h2>
									<div className={s.selection_part}>
										{ paymentMethod !== paymentMethodsMapping.klarnaCheckout && (
											<form onSubmit={onPurchase}>
												<div className={s.selection_part_address}>
													<ErrorBoundary message="Billing address" debugData={paymentFields}>
														{
															addressFieldOrder.map((field) => {
																if (field === 'country') return null;
																if (!paymentFields.address[field].visible) return null;
																if (field === 'state') {
																	if (states) {
																		return (
																			<div key={`address-${field}`} className={s.selection_part_address_row}>
																				<Select id={`address-${field}`} selected={checkout.payment.address[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.address[field]} />
																			</div>
																		);
																	}
																	return null;
																}
																let fieldType = 'text';
																if (field === 'email') fieldType = 'email';
																if (field === 'phoneNumber') fieldType = 'tel';
																const fieldEvents = {
																	inputChange: onAddressInputChange,
																	inputBlur: onAddressInputBlur,
																	isError: errors.address?.[field],
																};

																return (
																	<div key={`address-${field}`} className={s.selection_part_address_row}>
																		<Input id={`address-${field}`} type={fieldType} value={checkout.payment.address[field]} label={translate(setup, `label${upperCaseFirst(field)}`)} {...fieldEvents} />
																	</div>
																);
															})
														}
														{
															hasShipping && (
																<div className={s.selection_part_address_row}>
																	<Select id="address-country" selected={checkout.payment.address.country} options={countries} label={translate(setup, 'labelCountry')} inputChange={onCountryChange} />
																</div>
															)
														}
													</ErrorBoundary>
												</div>
												{
													hasShipping && (
														<div className={s.selection_part_shippingAddress}>
															<ErrorBoundary message="Shipping address" debugData={paymentFields}>
																<Checkbox id="useShipping" label={translate(setup, 'checkoutAlternateAddress')} selected={checkout.useShippingAddress} inputChange={onBooleanChange} />
																{
																	checkout.useShippingAddress && (
																		<div className={s.selection_part_shippingAddress_inner}>
																			{
																				addressFieldOrder.map((field) => {
																					if (!paymentFields.address[field].visible) return null;
																					if (field === 'state') {
																						if (states) {
																							return (
																								<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																									<Select id={`shippingAddress-${field}`} selected={checkout.payment.shippingAddress[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																								</div>
																							);
																						}
																						return null;
																					}
																					let fieldType = 'text';
																					if (field === 'email') fieldType = 'email';
																					if (field === 'phoneNumber') fieldType = 'tel';
																					return (
																						<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																							<Input id={`shippingAddress-${field}`} type={fieldType} value={checkout.payment.shippingAddress[field]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																						</div>
																					);
																				})
																			}
																			<div className={s.selection_part_address_row}>
																				<Select id="shippingAddress-country" selected={checkout.payment.shippingAddress.country} options={countries} label={translate(setup, 'labelCountry')} inputChange={onShippingCountryChange} />
																			</div>
																		</div>
																	)
																}
															</ErrorBoundary>
														</div>
													)
												}
												<div className={s.selection_part_purchase} ref={policyLinks}>
													<ErrorBoundary message="Complete payment" debugData={checkout.payment}>
														<Checkbox id="subscribeToNewsletter" selected={checkout.payment.address.newsletter} inputChange={onBooleanChange}>
															<span dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'newsletterTerms')) }} />
														</Checkbox>
														{/*
															checkout.payment.address.newsletter && (
																<div className={s.selection_part_purchase_interest}>
																	<Checkbox id="womenNewsletter" label={translate(setup, 'newsletterWomen')} selected={fields.interest.indexOf('women') !== -1} inputChange={newsletterInterestChangeHandler} />
																	<Checkbox id="menNewsletter" label={translate(setup, 'newsletterMen')} selected={fields.interest.indexOf('men') !== -1} inputChange={newsletterInterestChangeHandler} />
																</div>
															)
														*/}
														<Checkbox id="termsAndConditions" selected={checkout.payment.termsAndConditions} inputChange={onBooleanChange} isError={errors?.termsAndConditions}>
															<span dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutAcceptTerms')) }} />
														</Checkbox>
														<div className={s.selection_part_purchase_errors}>
															{ paymentMethod.indexOf('paypal') !== -1 && <div className={s.selection_part_purchase_error}>{ translate(setup, 'paypalMessage') }</div> }
															{ checkout.selectionError && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutGeneralError') }</div> }
															{ checkout.selectionErrorStock && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutStockError') }</div> }
														</div>
														{ (!checkout.adyenInitiated || checkout.selectionError || checkout.selectionErrorStock) && <Button variantClasses={checkout.payment.termsAndConditions ? ['button__inverse'] : []} textColor={checkout.payment.termsAndConditions ? '#FFF5CB' : ''} type="submit">{ translate(setup, 'checkoutContinue') }</Button> }
													</ErrorBoundary>
												</div>
											</form>
										)}
									</div>
									<h2 className={s.selection_title}><span>{ translate(setup, 'checkoutDeliveryTitle') }</span></h2>
									<div className={s.selection_part}>
										<div className={s.selection_part_shippingMethod} style={{ display: checkout.adyenInitiated ? 'block' : 'none' }}>
											{
												ingridExists ? (
													<ErrorBoundary message="Ingrid" debugData={selection}>
														<Ingrid shippingHTML={checkout.paymentReturn.shippingHTML} centraCheckoutScript={checkout.paymentReturn.centraCheckoutScript} ingridLoaded={props.ingridLoaded} ingridUnloaded={props.ingridUnloaded} token={selection.token} />
													</ErrorBoundary>
												) : (
													<ErrorBoundary message="Shipping methods" debugData={selection}>
														<div className={s.selection_part_shipping}>
															<Radio id="shipping" selected={selection.shippingMethod} options={availableShippingMethods} inputChange={onShippingMethodChange} />
														</div>
													</ErrorBoundary>
												)
											}
										</div>
									</div>
									<h2 className={s.selection_title}><span>{ translate(setup, 'checkoutPaymentTitle') }</span></h2>
									<div className={s.selection_part}>
										<div className={s.selection_part_paymentMethod}>
											{
												hasKlarna.length > 0 && (
													<div className={[s.selection_part_purchase, s.selection_part_purchase__klarna].join(' ')} style={{ display: (paymentMethod !== paymentMethodsMapping.klarnaCheckout ? 'none' : 'block') }}>
														<ErrorBoundary message="Klarna checkout" debugData={selection}>
															<KlarnaCheckout paymentMethod={paymentMethod} />
														</ErrorBoundary>
													</div>
												)
											}
											{
												(Object.keys(hasAdyenDropIn).length > 0 || Object.keys(hasAdyenCheckout).length > 0) && (
													<div className={[s.selection_part_paymentMethod_container, (paymentUpdating ? s.selection_part_paymentMethod_container__updating : null)].join(' ')} id="adyenDropInContainer" />
												)
											}
											{
												Object.keys(hasStripeCheckout).length > 0 && (
													<div className={[s.selection_part_paymentMethod_container, (paymentUpdating ? s.selection_part_paymentMethod_container__updating : null)].join(' ')} id="stripeCheckoutContainer" />
												)
											}
										</div>
									</div>
								</div>
							)
						}
					</div>
				)
			}
			<Overlay variantClasses={['overlay__selection']} isShowing={overlayShowing} closeOverlayHandler={closeOverlayHandler}>
				{ pageData[overlayShowing] && <TemplateParser sections={pageData[overlayShowing]?.content?.sections || []} setup={setup} isOverlay /> }
			</Overlay>
		</main>
	);
};

const mapState = ({ checkout, environment, pageData }, { data }) => (
	{ checkout, pageData, setup: environment.setup, data }
);

export default hot(module)(connect(mapState, {
	addVoucher,
	removeVoucher,
	initiatedScript,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	ingridLoaded,
	ingridUnloaded,
	updateKlarna,
	updatePaymentMethod,
	purchase,
	goToPage,
	preloadPage,
})(Selection));
